import { useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";

import {
  LandingPageKind,
  LandingPageStatus,
  landingPageConfigSchema,
  type LandingPage,
  type LandingPageConfig,
} from "@fitness-app/data-models/entities/LandingPage";
import {
  createFunnelPageConfig,
  getLandingTemplateSlug,
  type LandingTemplate,
} from "@fitness-app/data-models/landing-templates/index";
import { type SalesFunnel } from "@fitness-app/data-models/entities/SalesFunnel";

import { supabase } from "~/store/initializeStore";
import { createDefaultLandingConfig } from "./defaultLandingConfig";
import { FUNNEL_TEMPLATE_SLUG_PREFIX, normalizeFunnelPageSlug, normalizeLandingSlug } from "./landingSlug";

const LANDINGS_QUERY_KEY = ["landingPages"];
const FUNNEL_USAGE_QUERY_KEY = ["landingPages", "funnelUsage"];

/** A funnel step rendering a builder page (`stepConfig.landingPageId`). */
export interface LandingFunnelUsage {
  funnelId: string;
  funnelName: string;
  funnelSlug: string;
  productId: string;
  stepName: string;
}

/** Which funnel steps use which builder page, by page id. */
export const useLandingFunnelUsage = () =>
  useQuery(FUNNEL_USAGE_QUERY_KEY, {
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sales_funnel")
        .select("id,name,slug,productId,steps,status")
        .neq("status", "archived")
        .returns<Pick<SalesFunnel, "id" | "name" | "slug" | "productId" | "steps" | "status">[]>();

      if (error) {
        throw new Error(error.message);
      }

      const usage = new Map<string, LandingFunnelUsage[]>();
      for (const funnel of data ?? []) {
        for (const step of funnel.steps ?? []) {
          const pageId = step.stepConfig?.landingPageId;
          if (!pageId) continue;
          usage.set(pageId, [
            ...(usage.get(pageId) ?? []),
            {
              funnelId: funnel.id,
              funnelName: funnel.name || funnel.slug,
              funnelSlug: funnel.slug,
              productId: funnel.productId,
              stepName: step.name,
            },
          ]);
        }
      }
      return usage;
    },
  });

export const useLandings = () => {
  const queryClient = useQueryClient();

  const query = useQuery(LANDINGS_QUERY_KEY, {
    queryFn: async () => {
      const { data, error } = await supabase
        .from("landing_page")
        .select("*")
        .neq("status", LandingPageStatus.Archived)
        .order("updatedAt", { ascending: false, nullsFirst: false })
        .returns<LandingPage[]>();

      if (error) {
        throw new Error(error.message);
      }

      return data ?? [];
    },
  });

  const refetch = () => queryClient.invalidateQueries(LANDINGS_QUERY_KEY);

  const createLanding = async ({
    name,
    slug,
    kind = LandingPageKind.Landing,
  }: {
    name: string;
    slug: string;
    kind?: LandingPageKind;
  }) => {
    const isFunnelPage = kind !== LandingPageKind.Landing || slug.startsWith("szablony/");
    const config = isFunnelPage
      ? createFunnelPageConfig({
          title: name,
          pageName: slug.replace(/\W+/g, "_"),
          shell: kind === LandingPageKind.Landing ? "landing" : "embedding",
          sections: [],
        })
      : createDefaultLandingConfig(slug);
    const { error } = await supabase.from("landing_page").insert({
      name,
      slug,
      kind,
      status: LandingPageStatus.Draft,
      draftConfig: config,
      publishedConfig: null,
      metadata: null,
    });

    if (error) {
      throw new Error(error.message);
    }

    await refetch();
  };

  const saveDraft = async (id: string, draftConfig: LandingPageConfig, fields: { name: string; slug: string }) => {
    const { error } = await supabase
      .from("landing_page")
      .update({
        name: fields.name,
        slug: fields.slug,
        draftConfig,
        updatedAt: new Date().toISOString(),
      })
      .eq("id", id);

    if (error) {
      throw new Error(error.message);
    }

    await refetch();
  };

  const publish = async (
    landing: LandingPage,
    draftConfig: LandingPageConfig,
    fields: { name: string; slug: string },
  ) => {
    landingPageConfigSchema.parse(draftConfig);

    const { error } = await supabase
      .from("landing_page")
      .update({
        name: fields.name,
        slug: fields.slug,
        draftConfig,
        publishedConfig: draftConfig,
        publishedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        status: LandingPageStatus.Published,
      })
      .eq("id", landing.id);

    if (error) {
      throw new Error(error.message);
    }

    await refetch();
  };

  const duplicate = async (landing: LandingPage, options: { publish?: boolean } = {}) => {
    // Normalize so the copy lands on a routable slug (metamorfoza/… prefix) and
    // the "Kopiuj publiczny URL" link resolves immediately — otherwise the first
    // editor save would silently rewrite it and break any URL already shared.
    const suffixed = `${landing.slug || "home"}-${dayjs().format("YYYYMMDDHHmm")}`;
    // A funnel page (any kind but a landing, or a landing under szablony/) stays a funnel page.
    const isFunnelPage =
      (landing.kind ?? LandingPageKind.Landing) !== LandingPageKind.Landing ||
      landing.slug.startsWith(FUNNEL_TEMPLATE_SLUG_PREFIX);
    const slug = isFunnelPage ? normalizeFunnelPageSlug(suffixed) : normalizeLandingSlug(suffixed);
    // A copy published right away (for a funnel step) copies what is live, not unpublished edits.
    const publishedSource = landing.publishedConfig ?? landing.draftConfig;
    const { data, error } = await supabase
      .from("landing_page")
      .insert({
        name: `${landing.name} copy`,
        slug,
        kind: landing.kind ?? LandingPageKind.Landing,
        status: options.publish ? LandingPageStatus.Published : LandingPageStatus.Draft,
        draftConfig: options.publish ? publishedSource : landing.draftConfig,
        // A copy made for a funnel step is published right away so the step can render it.
        publishedConfig: options.publish ? publishedSource : null,
        publishedAt: options.publish ? new Date().toISOString() : null,
        metadata: landing.metadata,
      })
      .select("id")
      .single();

    if (error) {
      throw new Error(error.message);
    }

    await refetch();
    return data.id;
  };

  const archive = async (id: string) => {
    const { error } = await supabase
      .from("landing_page")
      .update({ status: LandingPageStatus.Archived, updatedAt: new Date().toISOString() })
      .eq("id", id);

    if (error) {
      throw new Error(error.message);
    }

    await refetch();
  };

  /**
   * Adds a built-in offer template (data-models `landing-templates`) as a published builder
   * page, ready to pick in a funnel step, or brings back its archived copy.
   */
  const importTemplate = async (template: LandingTemplate) => {
    const slug = getLandingTemplateSlug(template.key);
    const { data: existing, error: lookupError } = await supabase
      .from("landing_page")
      .select("status")
      .eq("slug", slug)
      .maybeSingle();
    if (lookupError) {
      throw new Error(lookupError.message);
    }
    // Never overwrite a page in use (it may hold an admin's edits); only an archived one comes back.
    if (existing && existing.status !== LandingPageStatus.Archived) {
      return;
    }

    const now = new Date().toISOString();
    // Upsert on the slug: an archived copy of the template (its slug is unique) comes back
    // published with the current template. Pages in use are never offered (`getTakenSlugs`).
    const { error } = await supabase.from("landing_page").upsert(
      {
        name: template.name,
        slug,
        kind: template.kind,
        status: LandingPageStatus.Published,
        draftConfig: template.config,
        publishedConfig: template.config,
        publishedAt: now,
        metadata: { templateKey: template.key, sourceRoute: template.sourceRoute },
        updatedAt: now,
      },
      { onConflict: "slug" },
    );

    if (error) {
      throw new Error(error.message);
    }
  };

  const importTemplates = async (templates: LandingTemplate[]) => {
    for (const template of templates) {
      await importTemplate(template);
    }
    await refetch();
  };

  /** Slugs of pages in use; an archived template can be added again (the import revives it). */
  const getTakenSlugs = async () => {
    const { data, error } = await supabase
      .from("landing_page")
      .select("slug")
      .neq("status", LandingPageStatus.Archived);
    if (error) {
      throw new Error(error.message);
    }
    return new Set((data ?? []).map((row) => row.slug));
  };

  return {
    ...query,
    createLanding,
    importTemplates,
    getTakenSlugs,
    saveDraft,
    publish,
    duplicate,
    archive,
  };
};
